import { isValidProfessionalCountInput } from "../../../utils/firmProfessionalCounts";
import { getAcceptedEmailDomainValidationError } from "../../../utils/emailValidation";
import { isValidWebsiteUrlInput } from "../../../utils/urlValidation";
import { getCreateFirmNameConflicts } from "../../../utils/firmNameUniqueness";
import { buildCreateFirmJurisdiction } from "./buildCreateFirmPayloads";
import type {
  CreateFirmFieldErrors,
  CreateFirmFormState,
} from "./createFirmFormTypes";

type ValidateCreateFirmFormOptions = {
  firmNameIndex?: Set<string>;
  /** True when the selected country has states in the catalog. */
  countryHasStates?: boolean;
  /** True when the selected state has cities in the catalog. */
  stateHasCities?: boolean;
};

export function validateCreateFirmForm(
  form: CreateFirmFormState,
  options?: ValidateCreateFirmFormOptions,
): CreateFirmFieldErrors {
  const errors: CreateFirmFieldErrors = {};

  if (!form.firmName.trim()) {
    errors.firmName = "Firm name is required.";
  }
  if (!form.serviceType) {
    errors.serviceType = "Service type is required.";
  }
  if (!form.country.id) {
    errors.country = "Country is required.";
  }
  if (options?.countryHasStates && !form.state.trim()) {
    errors.state = "State is required.";
  }
  if (options?.stateHasCities && !form.city.trim()) {
    errors.city = "City is required.";
  }
  if (!form.address1.trim()) {
    errors.address1 = "Address is required.";
  }
  if (form.country.id && !buildCreateFirmJurisdiction(form, "firm")) {
    errors.jurisdiction =
      "The selected country has no ISO code, so no jurisdiction can be built.";
  }
  if (form.logo == null) {
    errors.logo = "Logo is required.";
  }
  if (!form.firmOverview.trim()) {
    errors.firmOverview = "Firm overview is required.";
  }
  const yearFounded = form.yearFounded.trim();
  if (yearFounded && !/^\d{4}$/.test(yearFounded)) {
    errors.yearFounded = "Year founded must be a 4-digit year.";
  }
  if (!isValidProfessionalCountInput(form.totalProfessionals)) {
    errors.totalProfessionals = "Total professionals must be a whole number.";
  }
  if (!isValidProfessionalCountInput(form.professionalsInPrimary)) {
    errors.professionalsInPrimary =
      "Professionals in primary must be a whole number.";
  }
  if (!isValidWebsiteUrlInput(form.websiteUrl)) {
    errors.websiteUrl = "Website URL must be a valid URL.";
  }
  const invalidAcceptedEmailDomain = form.acceptedEmailDomain.find(
    (domain) => getAcceptedEmailDomainValidationError(domain) !== null,
  );
  if (invalidAcceptedEmailDomain) {
    errors.acceptedEmailDomain =
      getAcceptedEmailDomainValidationError(invalidAcceptedEmailDomain) ??
      "Enter a valid email domain (e.g. acme.com).";
  }

  if (options?.firmNameIndex) {
    Object.assign(
      errors,
      getCreateFirmNameConflicts(form, options.firmNameIndex),
    );
  }

  return errors;
}

export function hasCreateFirmFormErrors(
  errors: CreateFirmFieldErrors,
): boolean {
  return Object.keys(errors).length > 0;
}
